/**
 * Phase 4: dated, case-linked reminders. The CDL Legal and plan claims, condition and deferral
 * deadlines (Lloyd, Jul 21 2026: "Trigger on the first pre-trial is fine. And escalate if no one
 * acts."), and reminders staff set for themselves (Hannah, 20 Oct 2026).
 */

/** The type of a reminder a staff member set by hand. Mirrors STAFF_REMINDER on the backend. */
export const STAFF_REMINDER = 'staff_reminder';
export interface CaseReminderDto {
  CaseReminder_guid: string;
  CaseReminder_Type: string;
  CaseReminder_DueAt: string;
  CaseReminder_State: string;
  CaseReminder_EscalatedAt: string | null;
  CaseReminder_ResolvedAt: string | null;
  CaseReminder_ResolvedByName: string | null;
  CaseReminder_StoppedAt: string | null;
  /** Staff reminders only: the note as typed, and who set it. */
  CaseReminder_Note: string | null;
  CaseReminder_CreatedByName: string | null;
  title: string;
  body: string;
  Ticket_guid: string | null;
  Ticket_Number: string | null;
  clientName: string | null;
}

class CaseReminderApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  /** Everything awaiting action, most overdue first. */
  static async listOpen(): Promise<CaseReminderDto[]> {
    const response = await fetch(`${CaseReminderApi.host()}/api/v1/manager/case-reminders`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to load reminders');
    return response.json();
  }

  /** Reminders on one case, resolved ones included. */
  static async listForTicket(ticketGuid: string): Promise<CaseReminderDto[]> {
    const response = await fetch(
      `${CaseReminderApi.host()}/api/v1/manager/ticket/${ticketGuid}/case-reminders`,
      { method: 'GET', credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to load reminders for this case');
    return response.json();
  }

  /** Set a reminder on a case for yourself: the date it comes back (YYYY-MM-DD) and a note. */
  static async create(
    ticketGuid: string,
    input: { note: string; dueDate: string },
  ): Promise<CaseReminderDto> {
    const response = await fetch(
      `${CaseReminderApi.host()}/api/v1/manager/ticket/${ticketGuid}/case-reminder`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || 'Failed to set the reminder');
    }
    return response.json();
  }

  /** Mark it done (a claim filed, a check-in made): the only thing that takes it out of the list. */
  static async resolve(reminderGuid: string): Promise<CaseReminderDto> {
    const response = await fetch(
      `${CaseReminderApi.host()}/api/v1/manager/case-reminder/${reminderGuid}/resolve`,
      { method: 'POST', credentials: 'include' },
    );
    if (!response.ok) throw new Error('Failed to update the reminder');
    return response.json();
  }
}

export default CaseReminderApi;
